import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnChanges,
  SimpleChanges,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

/**
 * Reusable action button component that renders a configurable button with optional icon, count badge, variants, sizes, and accessibility support.
 * @component
 * @selector app-action-button
 * @example
 *   <app-action-button
 *     [buttonText]="'Save'"
 *     [iconSrc]="'/assets/save.svg'"
 *     [variant]="'primary'"
 *     [size]="'large'"
 *     [showCount]="true"
 *     [count]="3"
 *     (buttonClick)="onSave()">
 *   </app-action-button>
 * @input {string} buttonText - Text label displayed on the button (defaults to 'Action').
 * @input {string | undefined} iconSrc - URL to an icon image to display inside the button.
 * @input {string | undefined} iconSvg - Inline SVG string to render as the button icon (used when provided instead of iconSrc).
 * @input {'default'|'primary'|'secondary'|'outline'} variant - Visual variant of the button influencing styling (defaults to 'default').
 * @input {'small'|'medium'|'large'} size - Size variant of the button controlling padding and font sizing (defaults to 'medium').
 * @input {boolean} disabled - If true, the button is non-interactive and click events are suppressed (defaults to false).
 * @input {boolean} showCount - Whether to show a numeric count badge alongside the button (defaults to false).
 * @input {number} count - Numeric value displayed in the count badge when showCount is true.
 * @input {string | undefined} customClass - Additional custom CSS class(es) appended to the button's root element.
 * @input {string | undefined} ariaLabel - Accessible label for the button when the visible text is insufficient or absent.
 * @output {EventEmitter<void>} buttonClick - Emitted when the button is activated by the user (only emitted when not disabled).
 *
 * Key methods:
 * - onClick(): Handles user click interactions; emits buttonClick only if the component is not disabled.
 * - getButtonClass(): Computes and returns the final CSS class string for the button based on variant, size, count presence, icon-only state, and any customClass.
 *
 * Lifecycle hooks:
 * - ngOnInit(): Initialization hook used to perform any startup logic (currently reserved for initialization).
 */
@Component({
  selector: 'app-action-button',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './action-button.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./action-button.component.scss'],
})
export class ActionButtonComponent implements OnInit, OnChanges {
  @Input() buttonText = 'Action';
  @Input() iconSrc?: string;
  @Input() iconSvg?: string;

  safeSvg: SafeHtml | null = null;

  constructor(private sanitizer: DomSanitizer) {}
  @Input() variant: 'default' | 'primary' | 'secondary' | 'outline' = 'default';
  @Input() size: 'small' | 'medium' | 'large' = 'medium';
  @Input() disabled = false;
  @Input() showCount = false;
  @Input() count = 0;
  @Input() customClass?: string;
  @Input() ariaLabel?: string;

  @Output() buttonClick = new EventEmitter<void>();

  ngOnInit(): void {
    this.updateSafeSvg();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['iconSvg']) {
      this.updateSafeSvg();
    }
  }

  private updateSafeSvg(): void {
    // bypassSecurityTrustHtml is intentional here: iconSvg accepts only
    // developer-defined inline SVG strings (never raw user input), so XSS risk is absent.
    this.safeSvg = this.iconSvg ? this.sanitizer.bypassSecurityTrustHtml(this.iconSvg) : null;
  }

  onClick(): void {
    if (!this.disabled) {
      this.buttonClick.emit();
    }
  }

  /**
   * Build a space-delimited string of CSS classes for the action button based on component state.
   * @example
   * etButtonClass()
   * "action-button primary large has-count icon-only custom-class"
   * @returns {string} A space-separated list of CSS class names for the button.
   **/
  getButtonClass(): string {
    const classes = ['action-button'];

    if (this.variant !== 'default') {
      classes.push(this.variant);
    }

    if (this.size !== 'medium') {
      classes.push(this.size);
    }

    if (this.showCount && this.count > 0) {
      classes.push('has-count');
    }

    // Add icon-only class when there's no button text
    if (!this.buttonText && (this.iconSrc || this.iconSvg)) {
      classes.push('icon-only');
    }

    if (this.customClass) {
      classes.push(this.customClass);
    }

    return classes.join(' ');
  }
}
